import { DotText } from "@/v2-backoffice-client/components/dot"
import {
  formatCompactAmount,
  formatInvoiceAmount,
} from "@/v2-backoffice-client/customer/billing/customerBilling.utils"
import { useLicensePlanTermsQuery } from "@/v2-backoffice-client/license/plan/view/license.plan.terms.query"
import LicensePlanComparison from "@/v2-backoffice-client/license/plan/view/LicensePlanComparison"
import LicensePlanTermsTable from "@/v2-backoffice-client/license/plan/view/LicensePlanTermsTable"
import type { LicensePlanFragment } from "@/v2-backoffice-client/license/plan/__types__/license.plan.fragment"

export type LicensePlanRevenueProps = {
  plan: LicensePlanFragment
  /** The full catalog (the board query) — rank and share come from it. */
  plans: LicensePlanFragment[]
}

// The license panel's Revenue tab: how much the license bills (MRR/ARR), where it
// sits in the catalog (rank, share, comparison bars), what each customer
// contributes on average, how much of it is behind on payment — then the
// list prices underneath. The money facts (paying customers, MRR at risk)
// come from the license row itself — the same columns the Licenses board sorts on,
// so board and panel can never disagree. Owns the terms query (its sole
// consumer); the stat cards render immediately from the license row while the
// terms load.
function LicensePlanRevenue(props: LicensePlanRevenueProps) {
  const { plan, plans } = props

  const { data: termsData, loading: termsLoading } = useLicensePlanTermsQuery({
    variables: { planId: plan.Id },
  })
  const terms = termsData?.getLicensePlanTerms ?? []

  const mrr = plan.Mrr ?? 0
  const currency =
    plan.MrrCurrency ??
    terms.find((term) => term.Currency)?.Currency ??
    undefined

  // The billing catalog, biggest book first — rank and the comparison rows.
  const rankedPlans = plans
    .filter((candidate) => (candidate.Mrr ?? 0) > 0)
    .sort((a, b) => (b.Mrr ?? 0) - (a.Mrr ?? 0))
  const bookMrr = rankedPlans.reduce(
    (sum, ranked) => sum + (ranked.Mrr ?? 0),
    0,
  )
  const rank =
    mrr > 0
      ? rankedPlans.findIndex((candidate) => candidate.Id === plan.Id) + 1
      : null
  const bookSharePct = mrr > 0 && bookMrr > 0 ? (mrr / bookMrr) * 100 : null

  const payingCustomerCount = plan.PayingCustomerCount
  const avgMrrPerCustomer =
    payingCustomerCount > 0 ? mrr / payingCustomerCount : null

  const atRiskMrr = plan.MrrAtRisk ?? 0

  const liveSubscriptionCount = terms.reduce(
    (sum, term) => sum + term.SubscriptionCount,
    0,
  )

  return (
    <div className="flex flex-col gap-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <section className="flex flex-col gap-1 border-l-2 border-border pl-3">
          <h3 className="text-xs text-muted-foreground">Monthly run-rate</h3>
          <p className="text-sm font-semibold tabular-nums">
            {mrr > 0 ? formatInvoiceAmount(mrr, currency) : "Not billing"}
          </p>
          {mrr > 0 ? (
            <p className="text-xs text-muted-foreground">
              ≈ {formatCompactAmount(mrr * 12, currency)} a year
              {!termsLoading &&
                ` · ${liveSubscriptionCount} live subscription${liveSubscriptionCount === 1 ? "" : "s"}`}
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">
              No live Fenerum subscriptions ride this license's terms.
            </p>
          )}
        </section>

        <section className="flex flex-col gap-1 border-l-2 border-border pl-3">
          <h3 className="text-xs text-muted-foreground">Catalog rank</h3>
          {rank ? (
            <>
              <p className="text-sm font-semibold">
                #{rank} of {rankedPlans.length}
              </p>
              <p className="text-xs text-muted-foreground">
                {bookSharePct != null &&
                  `${bookSharePct < 1 ? "<1" : Math.round(bookSharePct)} % of the live book (${formatCompactAmount(bookMrr, currency)}/mo)`}
              </p>
            </>
          ) : (
            <>
              <p className="text-sm font-semibold text-muted-foreground">
                Unranked
              </p>
              <p className="text-xs text-muted-foreground">
                Only billing licenses are ranked.
              </p>
            </>
          )}
        </section>

        <section className="flex flex-col gap-1 border-l-2 border-border pl-3">
          <h3 className="text-xs text-muted-foreground">Per paying customer</h3>
          <p className="text-sm font-semibold tabular-nums">
            {avgMrrPerCustomer != null
              ? `${formatInvoiceAmount(avgMrrPerCustomer, currency)}/mo`
              : "—"}
          </p>
          <p className="text-xs text-muted-foreground">
            {payingCustomerCount} paying customer
            {payingCustomerCount === 1 ? "" : "s"}
          </p>
        </section>

        <section className="flex flex-col gap-1 border-l-2 border-border pl-3">
          <h3 className="text-xs text-muted-foreground">At collection risk</h3>
          {atRiskMrr > 0 ? (
            <>
              <DotText variant="destructive" className="text-sm font-semibold">
                {formatInvoiceAmount(atRiskMrr, currency)}/mo
              </DotText>
              <p className="text-xs text-muted-foreground">
                {plan.AtRiskCustomerCount} customer
                {plan.AtRiskCustomerCount === 1 ? "" : "s"} behind on payment —
                see the Customers tab.
              </p>
            </>
          ) : payingCustomerCount > 0 ? (
            <>
              <DotText variant="success-1" className="text-sm font-semibold">
                Nothing
              </DotText>
              <p className="text-xs text-muted-foreground">
                No paying customer on this license is behind.
              </p>
            </>
          ) : (
            <>
              <p className="text-sm font-semibold text-muted-foreground">—</p>
              <p className="text-xs text-muted-foreground">
                No paying customers.
              </p>
            </>
          )}
        </section>
      </div>

      {rank != null && (
        <LicensePlanComparison
          plan={plan}
          rankedPlans={rankedPlans}
          currency={currency}
        />
      )}

      <LicensePlanTermsTable terms={terms} isLoading={termsLoading} />
    </div>
  )
}

export default LicensePlanRevenue
